import { useState } from 'react'
import { Check, X, CreditCard, Star, Zap, Shield, Crown, Monitor, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'

// ⚠️ CONFIGURATION: Replace these URLs with your actual Stripe Payment Links
// ⚠️ BELANGRIJK: Vul hier je Stripe Price ID's in (begint met price_...)
const PRICE_ID_MONTHLY = "price_1SfLZQKcWBh9Je2KwNGy0JJH"
const PRICE_ID_YEARLY = "price_1SfLc3KcWBh9Je2KiDuKF4Va"

export const UpgradePage = () => {
    const { t } = useTranslation()
    const { activeTenant } = useTenant()
    const { user } = useAuth()
    const [loading, setLoading] = useState(false)
    const [annual, setAnnual] = useState(true)
    const navigate = useNavigate()

    const handleUpgrade = async (plan) => {
        if (plan === 'free') return

        setLoading(true)

        try {
            // Bepaal de juiste prijs
            const priceId = annual ? PRICE_ID_YEARLY : PRICE_ID_MONTHLY

            // Roep de Backend aan (Veilig & Dynamisch)
            const { data, error } = await supabase.functions.invoke('create-checkout', {
                body: {
                    priceId: priceId,
                    userId: user.id,
                    tenantId: activeTenant.id,
                    successUrl: window.location.origin + '/admin/upgrade?payment=success',
                    cancelUrl: window.location.origin + '/admin/upgrade'
                }
            })

            if (error) throw error

            // Redirect naar Stripe Checkout
            if (data?.url) {
                window.location.href = data.url
            } else {
                throw new Error(t('upgrade.error_no_url'))
            }

        } catch (err) {
            console.error('Betaalfout:', err)
            alert(t('upgrade.error_checkout') + err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50/50">
            <div className="premium-container pt-8 md:pt-12">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-8">
                    <ArrowLeft size={16} className="mr-2" /> {t('common.back')}
                </Button>

                {window.location.search.includes('payment=success') && (
                    <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-8 rounded-xl shadow-sm flex items-start gap-4 animate-slideUp">
                        <div className="bg-white p-2 rounded-full text-green-500 shadow-sm">
                            <Check size={20} />
                        </div>
                        <div>
                            <p className="font-bold text-lg">{t('upgrade.payment_success_title')}</p>
                            <p className="text-sm opacity-90">{t('upgrade.payment_success_body')}</p>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="text-center mb-16 animate-slideUp">
                    <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-yellow-100 to-orange-100 mb-6 shadow-sm transform hover:scale-110 transition-transform duration-300">
                        <Crown className="text-yellow-600 w-12 h-12" />
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
                        {t('upgrade.title', { plan: '' })} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">{t('upgrade.premium')}</span>
                    </h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium">
                        {t('upgrade.subtitle')}
                    </p>

                    {/* Toggle */}
                    <div className="flex justify-center mt-12">
                        <div className="bg-white p-1.5 rounded-full border border-gray-200 shadow-sm flex relative cursor-pointer select-none">
                            <button
                                onClick={() => setAnnual(false)}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all z-10 w-36 ${!annual ? 'text-white' : 'text-gray-500 hover:text-gray-900'}`}
                            >
                                {t('upgrade.monthly')}
                            </button>
                            <button
                                onClick={() => setAnnual(true)}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all z-10 w-44 flex items-center justify-center gap-2 ${annual ? 'text-white' : 'text-gray-500 hover:text-gray-900'}`}
                            >
                                {t('upgrade.yearly')}
                                <span className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${annual ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'}`}>{t('upgrade.save_percent')}</span>
                            </button>
                            <div className={`absolute top-1.5 bottom-1.5 bg-gray-900 rounded-full transition-all duration-300 shadow-md ${annual ? 'left-[9rem] w-44 translate-x-[-144px] ml-[144px]' : 'left-1.5 w-36'}`}></div>
                            {/* Correction on slider logic, simplistic absolute positioning might fail on resize. Using standard transform is safer but tricky with different widths. 
                                Let's stick to simple conditional rendering for the bg or use a slightly better layout.
                                Improved logic below:
                            */}
                            <div className={`absolute top-1.5 bottom-1.5 bg-gray-900 rounded-full transition-all duration-300 shadow-md transform ${annual ? 'translate-x-[148px] w-[170px]' : 'translate-x-0 w-[145px]'}`} style={{ left: '6px' }}></div>
                            {/* Overwrite the buttons z-index to be on top */}
                        </div>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mb-24 max-w-7xl mx-auto">

                    {/* Free Tier */}
                    <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl hover:shadow-2xl transition-all relative z-0 flex flex-col animate-slideUp" style={{ animationDelay: '0.1s' }}>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('upgrade.plan_basic_title')}</h3>
                        <p className="text-gray-500 text-sm mb-6 font-medium">{t('upgrade.plan_basic_subtitle')}</p>
                        <div className="mb-8 p-4 bg-gray-50 rounded-2xl text-center">
                            <span className="text-4xl font-black text-gray-900">{t('upgrade.plan_basic_price')}</span>
                        </div>
                        <ul className="space-y-4 mb-8 flex-1">
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_max_1_tournament')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_max_8_teams')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_poule_knockout')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-400 text-sm line-through decoration-gray-300">
                                <div className="bg-gray-100 p-1 rounded-full"><X size={14} className="text-gray-400" /></div>
                                <span>{t('upgrade.feature_no_tv_mode')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-400 text-sm line-through decoration-gray-300">
                                <div className="bg-gray-100 p-1 rounded-full"><X size={14} className="text-gray-400" /></div>
                                <span>{t('upgrade.feature_no_ads_free')}</span>
                            </li>
                        </ul>
                        <Button variant="outline" className="w-full py-4 border-2 font-bold text-gray-400 bg-gray-50 hover:bg-gray-50 cursor-default">{t('upgrade.btn_current_plan')}</Button>
                    </div>

                    {/* Pro Tier (Highlighted) */}
                    <div className="bg-white rounded-[2rem] p-8 border-2 border-purple-500 shadow-2xl relative z-10 transform md:-translate-y-6 flex flex-col animate-scaleIn">
                        <div className="absolute top-0 right-0 left-0 h-2 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-t-[2rem]"></div>
                        <div className="absolute top-2 right-8 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-[10px] font-black px-3 py-1 rounded-b-xl uppercase tracking-wider shadow-sm">
                            {t('upgrade.plan_premium_most_popular')}
                        </div>

                        <div className="flex items-center gap-3 mb-2 mt-2">
                            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 p-2 rounded-xl">
                                <Crown size={24} className="text-indigo-600" />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900">{t('upgrade.plan_premium_title')}</h3>
                        </div>
                        <p className="text-gray-500 text-sm mb-8 font-medium">{t('upgrade.plan_premium_subtitle')}</p>

                        <div className="mb-8 text-center bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-100">
                            <div className="flex items-baseline justify-center gap-1">
                                <span className="text-5xl font-black text-gray-900 tracking-tight">{annual ? '€8,25' : '€9,95'}</span>
                                <span className="text-gray-500 font-bold">/{t('upgrade.monthly').toLowerCase().slice(0, 3)}</span>
                            </div>
                            {annual && <p className="text-xs text-green-600 font-bold mt-2 bg-green-100 inline-block px-3 py-1 rounded-full">{t('upgrade.save_amount')}</p>}
                        </div>

                        <ul className="space-y-4 mb-8 flex-1">
                            <li className="flex items-center gap-3 text-gray-800 font-medium text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_unlimited_tournaments')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-800 font-medium text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_unlimited_teams')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-800 font-medium text-sm">
                                <div className="bg-purple-100 p-1 rounded-full"><Monitor size={14} className="text-purple-600" /></div>
                                <span>{t('upgrade.feature_tv_mode')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-800 font-medium text-sm">
                                <div className="bg-yellow-100 p-1 rounded-full"><Shield size={14} className="text-yellow-600" /></div>
                                <span>{t('upgrade.feature_branding')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-800 font-medium text-sm">
                                <div className="bg-blue-100 p-1 rounded-full"><Zap size={14} className="text-blue-600" /></div>
                                <span>{t('upgrade.feature_ai_functions')}</span>
                            </li>
                        </ul>

                        <Button
                            className="w-full mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-xl shadow-purple-200 py-6 text-lg font-bold rounded-2xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
                            onClick={() => handleUpgrade('premium')}
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                            ) : (
                                t('upgrade.btn_upgrade_now')
                            )}
                        </Button>
                        <p className="text-center text-xs text-gray-400 font-medium mt-3">
                            {t('upgrade.trust_badge')}
                        </p>
                        <p className="text-center text-[10px] text-gray-400 mt-2 leading-tight px-4">
                            {t('upgrade.terms_prefix')} <a href="https://www.propoule.com/terms" target="_blank" className="underline hover:text-gray-600">{t('upgrade.terms_link')}</a> {t('upgrade.terms_and')} <a href="https://www.propoule.com/privacy" target="_blank" className="underline hover:text-gray-600">{t('upgrade.privacy_link')}</a>.
                        </p>
                    </div>

                    {/* Enterprise Tier */}
                    <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl hover:shadow-2xl transition-all relative z-0 flex flex-col animate-slideUp" style={{ animationDelay: '0.2s' }}>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('upgrade.plan_enterprise_title')}</h3>
                        <p className="text-gray-500 text-sm mb-6 font-medium">{t('upgrade.plan_enterprise_subtitle')}</p>
                        <div className="mb-8 p-4 bg-gray-50 rounded-2xl text-center">
                            <span className="text-3xl font-black text-gray-900">{t('upgrade.plan_enterprise_price')}</span>
                        </div>
                        <ul className="space-y-4 mb-8 flex-1">
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_multi_admin')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_white_label')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_api_access')}</span>
                            </li>
                            <li className="flex items-center gap-3 text-gray-600 text-sm">
                                <div className="bg-green-100 p-1 rounded-full"><Check size={14} className="text-green-600" /></div>
                                <span>{t('upgrade.feature_sponsor_plus')}</span>
                            </li>
                        </ul>
                        <Button variant="outline" className="w-full py-4 border-2 font-bold hover:bg-gray-50 text-gray-700" onClick={() => window.location.href = 'mailto:sales@propoule.com'}>{t('upgrade.btn_contact_sales')}</Button>
                    </div>
                </div>

                {/* FAQ / Trust */}
                <div className="text-center text-gray-400 text-sm pb-12 animate-fadeIn">
                    <p className="mb-4 font-medium text-gray-500">{t('upgrade.social_proof')}</p>
                    <div className="flex flex-wrap justify-center gap-6 opacity-80 text-xs font-bold uppercase tracking-wider">
                        <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> {t('upgrade.footer_secure')}</span>
                        <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> {t('upgrade.footer_instant')}</span>
                        <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> {t('upgrade.footer_invoice')}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
